import React from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { Dispatch } from 'redux';
import {
    selectTicketKeysMineSorted,
    selectTicketsListTabSelected,
    selectTicketState,
    selectAssignee,
    selectTicketViewIsMounted,
} from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { updateTicketState } from 'flow/actions/tickets-action';
import {
    TICKETS_LIST_TABS_KEYS,
    getEntityKeyNextKey,
} from 'flow/utils/ticket-utils';
import { history } from 'flow/configure-store';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { segmentTrack } from 'flow/actions/segment-actions';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { State } from 'flow/types/State';
import { TicketKey } from 'flow/types/EntityKeys';
import useAppContext from 'flow/hooks/useAppContext';
import MenuItem from 'flow/components/ui-components/MenuItem';
import useTicketAction from 'flow/hooks/useTicketAction';

type Props = {
    ticketKey: TicketKey;
};

const handlePostStatusChangeNavigation =
    (ticketKey: TicketKey, newStatus: 'open' | 'closed') =>
    (dispatch: Dispatch<any>, getState: () => State) => {
        const state = getState();
        const tabSelected = selectTicketsListTabSelected(state);
        const assignee = selectAssignee(state, ticketKey);
        const ownExtensionKey = selectOwnExtensionKey(state);
        const ticketKeysMyTicketsTab = selectTicketKeysMineSorted(state);
        const ticketViewIsMounted = selectTicketViewIsMounted(state, ticketKey);

        if (
            tabSelected === TICKETS_LIST_TABS_KEYS.MY_TICKETS &&
            assignee === ownExtensionKey &&
            ticketViewIsMounted &&
            newStatus === 'closed'
        ) {
            const nextKey = getEntityKeyNextKey(
                ticketKeysMyTicketsTab,
                ticketKey,
            );
            if (nextKey) {
                history.push(
                    generatePath(routes['tickets.ticketKey'], {
                        ticketKey: nextKey,
                    }),
                );
            } else {
                history.push(routes['tickets']);
            }
        }
    };

const MenuItemTicketStatus = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();

    const currentStatus = useAppSelector((state) =>
        selectTicketState(state, ticketKey),
    );
    const canSetState = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'setState'),
    );
    const actionType = currentStatus === 'open' ? 'close' : 'open';

    const handleClickTicketStatus = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (!canSetState) {
            return;
        }

        const newStatus = actionType === 'open' ? 'open' : 'closed';

        dispatch(
            segmentTrack(TICKET_STRINGS.USER_CHANGED_STATUS, {
                component: 'ticket context menu',
            }),
        );
        runTicketAction(updateTicketState(ticketKey, newStatus)).then(() => {
            dispatch(handlePostStatusChangeNavigation(ticketKey, newStatus));
        });
    };

    return (
        <MenuItem
            disabled={!canSetState}
            tooltipLabel={
                canSetState ? undefined : i18next.t('tickets.no_permission')
            }
            onClick={handleClickTicketStatus}
        >
            <span>
                {actionType === 'open'
                    ? i18next.t('chat.open-verb')
                    : i18next.t('tickets.close-verb')}
            </span>
        </MenuItem>
    );
};

export default MenuItemTicketStatus;
